// /src/components/DemoBlockModal.jsx
import React from "react";
import { useTranslation } from "react-i18next";

// Разрешаем только безопасные переходы: относительные или https того же origin
const safeNavigate = (url) => {
  try {
    const u = new URL(url, window.location.href);
    if (u.protocol !== "https:" && u.origin !== window.location.origin) return false;
    window.location.assign(u.toString());
    return true;
  } catch {
    // относительный путь без протокола тоже ок
    if (typeof url === "string" && url.startsWith("/")) {
      window.location.assign(url);
      return true;
    }
    return false;
  }
};

export default function DemoBlockModal({
  open,
  action = "",
  onClose = () => {},
  onRegister,            // кастомный обработчик клика "Регистрация" (необязателен)
  registrationUrl = "/signup", // куда вести по умолчанию
}) {
  const modalRef = React.useRef(null);
  const { t } = useTranslation("common");

  // A11y: фокус/Tab-трап + Esc для закрытия
  React.useEffect(() => {
    if (!open) return;
    const root = modalRef.current;
    if (!root) return;

    const prevActive = document.activeElement;

    const focusables = () =>
      Array.from(
        root.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));

    const list = focusables();
    if (list.length) list[0].focus();
    else {
      root.setAttribute("tabindex", "-1");
      root.focus();
    }

    const onKey = (e) => {
      if (e.key === "Escape") {
        try { onClose?.(); } catch {}
      } else if (e.key === "Tab") {
        const els = focusables();
        if (!els.length) return;
        const first = els[0];
        const last = els[els.length - 1];
        const active = document.activeElement;
        if (e.shiftKey) {
          if (active === first || !root.contains(active)) {
            last.focus(); e.preventDefault();
          }
        } else {
          if (active === last) {
            first.focus(); e.preventDefault();
          }
        }
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prevActive && typeof prevActive.focus === "function") prevActive.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const title = t(`demo_modal.actions.${action}.title`, {
    defaultValue: t("demo_modal.actions.default.title"),
  });
  const hint = t(`demo_modal.actions.${action}.hint`, {
    defaultValue: t("demo_modal.actions.default.hint"),
  });
  const header = t("demo_modal.header", { action: title });
  const intro = t("demo_modal.intro");
  const bulletsRaw = t("demo_modal.bullets", { returnObjects: true });
  const bullets = Array.isArray(bulletsRaw) ? bulletsRaw : [];
  const cancelLabel = t("demo_modal.buttons.cancel");
  const registerLabel = t("demo_modal.buttons.register");
  const closeAria = t("demo_modal.close.aria");
  const closeTitle = t("demo_modal.close.title");
  const registerTitle = t("demo_modal.register_title");

  const handleRegister = () => {
    if (typeof onRegister === "function") {
      try { onRegister(); return; } catch {}
    }
    safeNavigate(registrationUrl);
  };

  return (
    <div
      className="demo-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        className="demo-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="demo-modal-title"
        aria-describedby="demo-modal-body"
        ref={modalRef}
        style={{
          width: "min(92vw, 560px)",
          background: "#fff",
          borderRadius: 14,
          boxShadow: "0 12px 32px rgba(0,0,0,.25)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div
          style={{
            background: "#00723a",
            color: "#fff",
            padding: "16px 22px",
            fontWeight: 800,
            fontSize: 20,
            letterSpacing: ".3px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span id="demo-modal-title">{header}</span>
          <button
            onClick={onClose}
            type="button"
            aria-label={closeAria}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 28,
              cursor: "pointer",
              marginRight: -8,
              marginTop: -4,
              fontWeight: 400,
            }}
            title={closeTitle}
          >
            ×
          </button>
        </div>

        {/* BODY */}
        <div id="demo-modal-body" style={{ padding: "20px 22px 18px", fontSize: 16, color: "#333" }}>
          <p style={{ margin: "0 0 10px" }}>{intro}</p>
          <p style={{ margin: "0 0 14px" }}>{hint}</p>
          <ul style={{ margin: "0 0 14px 18px", padding: 0 }}>
            {bullets.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>

          {/* КНОПКИ */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 18px",
                fontSize: 16,
                borderRadius: 8,
                border: "1px solid #ddd",
                background: "#fff",
                cursor: "pointer",
              }}
            >
              {cancelLabel}
            </button>
            <button
              onClick={handleRegister}
              style={{
                padding: "10px 18px",
                fontSize: 16,
                borderRadius: 8,
                border: "none",
                background: "#00723a",
                color: "#fff",
                fontWeight: 700,
                cursor: "pointer",
              }}
              title={registerTitle}
            >
              {registerLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
